import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { jsPDF } from 'jspdf';
import QRCode from 'qrcode';
import JSZip from 'jszip';
import { Document, Page, pdfjs } from 'react-pdf';
import { Download, ChevronLeft, ZoomIn, ZoomOut, Printer } from 'lucide-react';
import { supabase } from '../utils/supabaseClient';
import TemplateEditor from './TemplateEditor';
import TemplateControlsPanel from './TemplateControlsPanel';
import { useTemplateConfig } from '../hooks/useTemplateConfig';
import { createDefaultConfig, normalizeTemplateConfig } from '../utils/templateConfigUtils';
import '../styles/ArtworkPdfGenerator.css';
import '../styles/TemplateEditor.css';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import micIcon from '../assets/mic.png';

// Set up PDF.js worker using local file
pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs';

// Avery Template Presets with Layout Specifications
const AVERY_TEMPLATES = {
  '5160': {
    name: 'Avery 5160 (1" × 2.625")',
    cardWidth: 2.625,
    cardHeight: 1,
    cardsPerRow: 3,
    cardsPerCol: 10,
    margin: 0.438,
    gap: 0,
    precut: true,
    layout: {
      titleFontSize: 12,
      titleFontStyle: 'bold',
      titleX: 0.1,
      titleY: 0.15,
      titleFitMode: 'wrap',

      artistFontSize: 10,
      artistFontStyle: 'italic',
      artistX: 0.1,
      artistY: 0.32,

      infoFontSize: 10,
      infoFontStyle: 'normal',
      mediumX: 0.1,
      mediumY: 0.45,
      completedDateX: 0.1,
      completedDateY: 0.55,
      yearY: 0.55,
      dimensionsX: 0.1,
      dimensionsY: 0.65,
      priceFontSize: 12,
      priceX: 0.1,
      priceY: 0.75,

      qrCodeSize: 0.5,
      qrCodeX: 2.05,
      qrCodeY: 0.15,
      contentStartX: 0.1,
      contentEndX: 1.95
    }
  },
  '5161': {
    name: 'Avery 5161 (1" × 4")',
    cardWidth: 4,
    cardHeight: 1,
    cardsPerRow: 2,
    cardsPerCol: 10,
    margin: 0.438,
    gap: 0,
    precut: true,
    layout: {
      titleFontSize: 12,
      titleFontStyle: 'bold',
      titleX: 0.1,
      titleY: 0.15,
      titleFitMode: 'wrap',

      artistFontSize: 10,
      artistFontStyle: 'italic',
      artistX: 0.1,
      artistY: 0.32,

      infoFontSize: 10,
      infoFontStyle: 'normal',
      mediumX: 0.1,
      mediumY: 0.45,
      completedDateX: 0.1,
      completedDateY: 0.55,
      yearY: 0.55,
      dimensionsX: 0.1,
      dimensionsY: 0.65,
      priceFontSize: 12,
      priceX: 0.1,
      priceY: 0.75,

      qrCodeSize: 0.5,
      qrCodeX: 3.3,
      qrCodeY: 0.15,
      contentStartX: 0.1,
      contentEndX: 3.2
    }
  },
  '5162': {
    name: 'Avery 5162 (1.5" × 4")',
    cardWidth: 4,
    cardHeight: 1.5,
    cardsPerRow: 2,
    cardsPerCol: 6,
    margin: 0.438,
    gap: 0,
    precut: true,
    layout: {
      titleFontSize: 14,
      titleFontStyle: 'bold',
      titleX: 0.1,
      titleY: 0.2,
      titleFitMode: 'wrap',

      artistFontSize: 11,
      artistFontStyle: 'italic',
      artistX: 0.1,
      artistY: 0.4,

      infoFontSize: 10,
      infoFontStyle: 'normal',
      mediumX: 0.1,
      mediumY: 0.55,
      completedDateX: 0.1,
      completedDateY: 0.7,
      yearY: 0.7,
      dimensionsX: 0.1,
      dimensionsY: 0.85,
      priceFontSize: 12,
      priceX: 0.1,
      priceY: 1.0,

      qrCodeSize: 0.6,
      qrCodeX: 3.2,
      qrCodeY: 0.2,
      contentStartX: 0.1,
      contentEndX: 3.1
    }
  },
  '8160': {
    name: 'Avery 8160 (1" × 2.625")',
    cardWidth: 2.625,
    cardHeight: 1,
    cardsPerRow: 3,
    cardsPerCol: 10,
    margin: 0.438,
    gap: 0,
    precut: true,
    layout: {
      titleFontSize: 12,
      titleFontStyle: 'bold',
      titleX: 0.1,
      titleY: 0.15,
      titleFitMode: 'wrap',

      artistFontSize: 10,
      artistFontStyle: 'italic',
      artistX: 0.1,
      artistY: 0.32,

      infoFontSize: 10,
      infoFontStyle: 'normal',
      mediumX: 0.1,
      mediumY: 0.45,
      completedDateX: 0.1,
      completedDateY: 0.55,
      yearY: 0.55,
      dimensionsX: 0.1,
      dimensionsY: 0.65,
      priceFontSize: 12,
      priceX: 0.1,
      priceY: 0.75,

      qrCodeSize: 0.5,
      qrCodeX: 2.05,
      qrCodeY: 0.15,
      contentStartX: 0.1,
      contentEndX: 1.95
    }
  },
  '8163': {
    name: 'Avery 8163 (2" × 4")',
    cardWidth: 4,
    cardHeight: 2,
    cardsPerRow: 2,
    cardsPerCol: 5,
    margin: 0.438,
    gap: 0,
    precut: true,
    layout: {
      titleFontSize: 14,
      titleFontStyle: 'bold',
      titleX: 0.1,
      titleY: 0.2,
      titleFitMode: 'wrap',

      artistFontSize: 11,
      artistFontStyle: 'italic',
      artistX: 0.1,
      artistY: 0.4,

      infoFontSize: 10,
      infoFontStyle: 'normal',
      mediumX: 0.1,
      mediumY: 0.55,
      completedDateX: 0.1,
      completedDateY: 0.7,
      yearY: 0.7,
      dimensionsX: 0.1,
      dimensionsY: 0.85,
      priceFontSize: 12,
      priceX: 0.1,
      priceY: 1.0,

      qrCodeSize: 0.6,
      qrCodeX: 3.2,
      qrCodeY: 0.2,
      contentStartX: 0.1,
      contentEndX: 3.1
    }
  },
  'custom_standard': {
    name: 'Custom Standard (3.5" × 2.5")',
    cardWidth: 3.5,
    cardHeight: 2.5,
    cardsPerRow: 2,
    cardsPerCol: 4,
    margin: 0.25,
    gap: 0.125,
    precut: false,
    layout: {
      titleFontSize: 16,
      titleFontStyle: 'bold',
      titleX: 0.15,
      titleY: 0.2,
      titleFitMode: 'wrap',

      artistFontSize: 12,
      artistFontStyle: 'italic',
      artistX: 0.15,
      artistY: 0.4,

      infoFontSize: 11,
      infoFontStyle: 'normal',
      mediumX: 0.15,
      mediumY: 0.6,
      completedDateX: 0.15,
      completedDateY: 0.8,
      yearY: 0.8,
      dimensionsX: 0.15,
      dimensionsY: 1.0,
      priceFontSize: 14,
      priceX: 0.15,
      priceY: 1.2,

      qrCodeSize: 0.7,
      qrCodeX: 2.6,
      qrCodeY: 0.2,
      contentStartX: 0.15,
      contentEndX: 2.5
    }
  },
  'custom_business': {
    name: 'Custom Business Card (3.5" × 2")',
    cardWidth: 3.5,
    cardHeight: 2,
    cardsPerRow: 2,
    cardsPerCol: 5,
    margin: 0.25,
    gap: 0.125,
    precut: false,
    layout: {
      titleFontSize: 14,
      titleFontStyle: 'bold',
      titleX: 0.15,
      titleY: 0.18,
      titleFitMode: 'wrap',

      artistFontSize: 11,
      artistFontStyle: 'italic',
      artistX: 0.15,
      artistY: 0.35,

      infoFontSize: 10,
      infoFontStyle: 'normal',
      mediumX: 0.15,
      mediumY: 0.5,
      completedDateX: 0.15,
      completedDateY: 0.65,
      yearY: 0.65,
      dimensionsX: 0.15,
      dimensionsY: 0.8,
      priceFontSize: 12,
      priceX: 0.15,
      priceY: 0.95,

      qrCodeSize: 0.6,
      qrCodeX: 2.6,
      qrCodeY: 0.18,
      contentStartX: 0.15,
      contentEndX: 2.5
    }
  },
  'thermal_4x6': {
    name: 'Thermal 4×6 (Shipping Label)',
    cardWidth: 4,
    cardHeight: 6,
    cardsPerRow: 1,
    cardsPerCol: 1,
    margin: 0,
    gap: 0,
    precut: true,
    isThermal: true,
    layout: {
      titleFontSize: 24,
      titleFontStyle: 'bold',
      titleX: 0.25,
      titleY: 0.5,
      titleFitMode: 'wrap',

      artistFontSize: 18,
      artistFontStyle: 'italic',
      artistX: 0.25,
      artistY: 1.0,

      infoFontSize: 14,
      infoFontStyle: 'normal',
      mediumX: 0.25,
      mediumY: 1.5,
      completedDateX: 0.25,
      completedDateY: 1.9,
      yearY: 1.9,
      dimensionsX: 0.25,
      dimensionsY: 2.3,
      priceFontSize: 22,
      priceX: 0.25,
      priceY: 2.8,

      qrCodeSize: 1.8,
      qrCodeX: 1.95,
      qrCodeY: 3.5,
      contentStartX: 0.25,
      contentEndX: 3.75,

      artworkNumberX: 0.25,
      artworkNumberY: 5.7,
      artworkNumberFontSize: 12
    }
  },
  'thermal_3x2': {
    name: 'Thermal 3×2 (Small Label)',
    cardWidth: 3,
    cardHeight: 2,
    cardsPerRow: 1,
    cardsPerCol: 1,
    margin: 0,
    gap: 0,
    precut: true,
    isThermal: true,
    layout: {
      titleFontSize: 14,
      titleFontStyle: 'bold',
      titleX: 0.1,
      titleY: 0.25,
      titleFitMode: 'wrap',

      artistFontSize: 10,
      artistFontStyle: 'italic',
      artistX: 0.1,
      artistY: 0.5,

      infoFontSize: 9,
      infoFontStyle: 'normal',
      mediumX: 0.1,
      mediumY: 0.7,
      completedDateX: 0.1,
      completedDateY: 0.9,
      yearY: 0.9,
      dimensionsX: 0.1,
      dimensionsY: 1.1,
      priceFontSize: 14,
      priceX: 0.1,
      priceY: 1.4,

      qrCodeSize: 0.7,
      qrCodeX: 2.15,
      qrCodeY: 0.2,
      contentStartX: 0.1,
      contentEndX: 2.0,

      artworkNumberX: 0.1,
      artworkNumberY: 1.85,
      artworkNumberFontSize: 8
    }
  }
};

const ArtworkPdfGenerator = () => {
  const { collectionId: collectionIdParam } = useParams();
  const navigate = useNavigate();

  const collectionId = collectionIdParam ? parseInt(collectionIdParam, 10) : null;

  const [artworks, setArtworks] = useState([]);
  const [walls, setWalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [showPrice, setShowPrice] = useState(true);
  const [pdfDataUrl, setPdfDataUrl] = useState(null);
  const [error, setError] = useState(null);
  const [collection, setCollection] = useState(null);
  const [artworksByWall, setArtworksByWall] = useState({});
  const [editDisplayText, setEditDisplayText] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [displayLocationModal, setDisplayLocationModal] = useState(null); // vc_id of artwork being edited
  
  // New font size state variables
  const [titleFontSize, setTitleFontSize] = useState(16);
  const [artistFontSize, setArtistFontSize] = useState(10);
  const [priceFontSize, setPriceFontSize] = useState(18);
  const [infoFontSize, setInfoFontSize] = useState(10);

  // New layout options
  const [selectedTemplate, setSelectedTemplate] = useState('custom_standard');
  const [showMedium, setShowMedium] = useState(true); // Show artwork medium
  const [showCompletedDate, setShowCompletedDate] = useState(true); // Show completion date
  const [titleFitModeState, setTitleFitModeState] = useState('wrap'); // 'wrap', 'shrink', or 'ellipsis'
  
  // Template configuration - uses localStorage (auto-saves on every change)
  const templateConfig = useTemplateConfig();
  const [currentTemplateConfig, setCurrentTemplateConfig] = useState(null);
  const [previewArtworkIndex, setPreviewArtworkIndex] = useState(0);
  const [previewImageUrl, setPreviewImageUrl] = useState(null); // Preview rendered as image for reliable QR display

  // Derive previewArtwork from index
  const previewArtwork = artworks.length > 0 ? artworks[previewArtworkIndex] : null;
  
  // PDF preview state
  const [numPages, setNumPages] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [scale, setScale] = useState(1.0);

  // Thermal printer export state
  const [generatingImages, setGeneratingImages] = useState(false);
  const [thermalImages, setThermalImages] = useState([]);
  const [showThermalExport, setShowThermalExport] = useState(false);
  const [imageGenProgress, setImageGenProgress] = useState({ current: 0, total: 0 });
  const [thermalTemplateSize, setThermalTemplateSize] = useState('thermal_3x2'); // 'thermal_3x2' or 'thermal_4x6'
  
  // Collapsible section states
  const [expandedSections, setExpandedSections] = useState({
    configDrawer: false   // Template Configuration drawer closed initially
  });
  
  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };
  
  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
    setCurrentPage(1);
  };

  // Reset preview index when artworks change
  useEffect(() => {
    if (artworks.length > 0 && previewArtworkIndex >= artworks.length) {
      setPreviewArtworkIndex(0);
    }
  }, [artworks.length, previewArtworkIndex]);

  // Load template configuration when selected template changes
  useEffect(() => {
    const loadConfig = () => {
      const template = AVERY_TEMPLATES[selectedTemplate];
      if (!template) {
        setCurrentTemplateConfig(null);
        return;
      }

      // Sync thermal template size when a thermal template is selected from main dropdown
      if (template.isThermal && (selectedTemplate === 'thermal_3x2' || selectedTemplate === 'thermal_4x6')) {
        setThermalTemplateSize(selectedTemplate);
      }

      const saved = templateConfig.getConfig(selectedTemplate);
      if (saved) {
        const normalized = normalizeTemplateConfig(template, saved, selectedTemplate);
        if (normalized) {
          setCurrentTemplateConfig(normalized);
          templateConfig.saveConfig(selectedTemplate, normalized);
          return;
        }
      }

      const defaultConfig = createDefaultConfig(template, selectedTemplate);
      setCurrentTemplateConfig(defaultConfig);
      templateConfig.saveConfig(selectedTemplate, defaultConfig);
    };
    loadConfig();
  }, [selectedTemplate]);

  // Generate a single-card preview as an image (not PDF) for reliable QR code display
  // PDF.js (used by react-pdf) doesn't reliably render embedded images from jsPDF,
  // so we render to canvas and draw the QR code directly like thermal images
  useEffect(() => {
    // Track if this effect is still current
    let isCurrent = true;

    const generatePreview = async () => {
      if (!previewArtwork || !currentTemplateConfig) {
        setPreviewImageUrl(null);
        return;
      }

      try {
        const template = AVERY_TEMPLATES[selectedTemplate];
        if (!template) {
          console.error('Template not found:', selectedTemplate);
          setPreviewImageUrl(null);
          return;
        }
        const { cardWidth, cardHeight } = template;

        // Generate QR code for this preview artwork
        let qrCodeData = null;
        try {
          const qrUrl = `https://share.vibrantcast.com/feelArtStory?id=${previewArtwork.vc_id}&cid=${collectionId}&inperson=true`;
          const qrDataUrl = await QRCode.toDataURL(qrUrl, { width: 200, margin: 1 });
          qrCodeData = { qrDataUrl, vc_id: previewArtwork.vc_id };
        } catch (err) {
          console.error('Error generating preview QR code:', err);
        }

        // Check if this effect is still current after async QR generation
        if (!isCurrent) return;

        // Ensure config has proper visibility defaults
        const configWithDefaults = {
          ...currentTemplateConfig,
          fieldVisibility: {
            showTitle: true,
            showArtist: true,
            showMedium: true,
            showCompletedDate: true,
            showDimensions: true,
            showPrice: true,
            showArtworkNumber: true,
            showQRCode: true,
            showCutLines: false,
            showPrintsAvailable: true,
            showAudioStory: true,
            ...currentTemplateConfig.fieldVisibility,
          }
        };

        // Create a PDF document for the label content
        const doc = new jsPDF({
          orientation: 'portrait',
          unit: 'in',
          format: [cardWidth, cardHeight],
        });

        // Add a subtle border so the preview shows the card edges
        doc.setDrawColor(180);
        doc.setLineWidth(0.01);
        doc.rect(0.02, 0.02, Math.max(0, cardWidth - 0.04), Math.max(0, cardHeight - 0.04));
        doc.setDrawColor(0);

        // Apply the template layout (text content) - skip QR code and audio icon since we draw them directly on canvas
        const previewConfig = {
          ...configWithDefaults,
          fieldVisibility: {
            ...configWithDefaults.fieldVisibility,
            showQRCode: false, // Don't draw QR in PDF - we'll draw it on canvas directly
            showAudioStory: false // Don't draw audio icon in PDF - we'll draw it on canvas directly
          }
        };
        applyTemplateLayout(doc, 0, 0, cardWidth, cardHeight, previewArtwork, qrCodeData, previewConfig);

        // Convert PDF to canvas using PDF.js, then draw QR code directly on canvas
        // This bypasses PDF.js image rendering issues
        const pdfBlob = doc.output('blob');
        const pdfUrl = URL.createObjectURL(pdfBlob);

        try {
          // Use higher DPI for crisp preview
          const DPI = 150;
          const widthPx = Math.round(cardWidth * DPI);
          const heightPx = Math.round(cardHeight * DPI);

          // Load and render PDF to canvas
          const loadingTask = pdfjs.getDocument(pdfUrl);
          const pdf = await loadingTask.promise;
          const page = await pdf.getPage(1);

          const viewport = page.getViewport({ scale: 1 });
          const scale = widthPx / viewport.width;
          const scaledViewport = page.getViewport({ scale });

          const canvas = document.createElement('canvas');
          canvas.width = widthPx;
          canvas.height = heightPx;
          const context = canvas.getContext('2d');

          // White background
          context.fillStyle = '#ffffff';
          context.fillRect(0, 0, widthPx, heightPx);

          await page.render({
            canvasContext: context,
            viewport: scaledViewport
          }).promise;

          // Draw QR code directly on canvas (bypasses PDF.js image rendering issues)
          const showQR = configWithDefaults.fieldVisibility?.showQRCode !== false;
          if (showQR && qrCodeData && qrCodeData.qrDataUrl) {
            try {
              const qrImg = new Image();
              await new Promise((resolve, reject) => {
                qrImg.onload = resolve;
                qrImg.onerror = reject;
                qrImg.src = qrCodeData.qrDataUrl;
              });

              // Calculate position in pixels based on layout config
              const layout = configWithDefaults.layout || currentTemplateConfig.layout || {};
              const qrX = (layout.qrCodeX || (cardWidth - 0.7)) * DPI;
              const qrY = (layout.qrCodeY || 0.2) * DPI;
              const qrSize = (layout.qrCodeSize || 0.5) * DPI;

              // Draw white background for QR code area
              context.fillStyle = '#ffffff';
              context.fillRect(qrX - 2, qrY - 2, qrSize + 4, qrSize + 4);

              // Draw QR code
              context.drawImage(qrImg, qrX, qrY, qrSize, qrSize);
            } catch (qrDrawError) {
              console.error('Error drawing QR code on preview canvas:', qrDrawError);
            }
          }

          // Draw Audio Story icon if artwork has audio
          const showAudioStory = configWithDefaults.fieldVisibility?.showAudioStory !== false;
          if (showAudioStory && previewArtwork.has_audio_story) {
            try {
              const micImg = new Image();
              await new Promise((resolve, reject) => {
                micImg.onload = resolve;
                micImg.onerror = reject;
                micImg.src = micIcon;
              });

              const layout = configWithDefaults.layout || currentTemplateConfig.layout || {};
              const micX = (layout.audioIconX || (cardWidth - 0.35)) * DPI;
              const micY = (layout.audioIconY || 0.15) * DPI;
              const micHeight = (layout.audioIconSize || 0.2) * DPI;
              
              // Calculate width based on natural aspect ratio
              const aspectRatio = micImg.naturalWidth / micImg.naturalHeight;
              const micWidth = micHeight * aspectRatio;

              context.drawImage(micImg, micX, micY, micWidth, micHeight);
            } catch (micDrawError) {
              console.error('Error drawing mic icon on preview canvas:', micDrawError);
            }
          }

          // Convert canvas to image data URL
          const imageDataUrl = canvas.toDataURL('image/png');

          if (isCurrent) {
            setPreviewImageUrl(imageDataUrl);
          }

          URL.revokeObjectURL(pdfUrl);
        } catch (renderError) {
          console.error('Error rendering preview to canvas:', renderError);
          URL.revokeObjectURL(pdfUrl);
          if (isCurrent) {
            setPreviewImageUrl(null);
          }
        }
      } catch (error) {
        console.error('Error generating preview:', error);
        if (isCurrent) {
          setPreviewImageUrl(null);
        }
      }
    };

    generatePreview();

    // Cleanup function to mark this effect as stale
    return () => {
      isCurrent = false;
    };
  }, [previewArtwork, currentTemplateConfig, selectedTemplate, titleFitModeState]);

  // Fetch collection data, walls, and artwork data
  useEffect(() => {
    const fetchData = async () => {
      if (!collectionId || isNaN(collectionId)) {
        console.warn('Invalid collectionId provided to ArtworkPdfGenerator:', collectionId);
        setError('Invalid collection ID');
        setLoading(false);
        return;
      }
      
      try {
        setLoading(true);
        
        // Import the API utility functions
        const { fetchCollectionDetails, fetchCollectionArtworks } = await import('../utils/api');
        
        // Fetch collection info
        const collectionData = await fetchCollectionDetails(collectionId);
        setCollection(collectionData);
        
        // Fetch wall elements from scene_elements table
        const { data: wallElements, error: wallError } = await supabase
          .from('scene_elements')
          .select('*')
          .eq('collection_id', collectionId)
          .eq('type', 'wall_2d')
          .order('display_order', { ascending: true });
          
        if (wallError) throw wallError;
        setWalls(wallElements || []);
        
        // Fetch artwork elements from scene_elements table to get parent_element_id relationships
        const { data: artworkElements, error: artworkElemError } = await supabase
          .from('scene_elements')
          .select('*')
          .eq('collection_id', collectionId)
          .eq('type', 'artwork')
          .eq('visible', true);
          
        if (artworkElemError) throw artworkElemError;
        
        // Create a mapping of vc_id to scene element data (incl. parent_element_id and text_content)
        const artworkElementsMap = {};
        artworkElements.forEach(elem => {
          artworkElementsMap[elem.vc_id] = elem;
          // Initialize display text edit state
          setEditDisplayText(prev => ({
            ...prev,
            [elem.vc_id]: elem.text_content || ''
          }));
        });
        
        // Fetch artworks for this collection
        const artworksData = await fetchCollectionArtworks(collectionId);
        
        // If no artworks found, set empty array
        if (!artworksData || artworksData.length === 0) {
          setArtworks([]);
          setLoading(false);
          return;
        }

        // Fetch print status for all artworks in this collection
        // Check for print_enabled OR on_demand_enabled = true
        const artworkVcIds = artworksData.map(a => a.vc_id);
        const { data: printStatusData, error: printStatusError } = await supabase
          .from('artwork_print_status')
          .select('vc_id, print_enabled, on_demand_enabled')
          .in('vc_id', artworkVcIds)
          .or('print_enabled.eq.true,on_demand_enabled.eq.true');

        if (printStatusError) {
          console.warn('Error fetching print status:', printStatusError);
        }

        // Create a map of vc_id to prints availability
        // prints_available = true if print_enabled OR on_demand_enabled is true
        const printStatusMap = {};
        (printStatusData || []).forEach(ps => {
          printStatusMap[ps.vc_id] = ps.print_enabled === true || ps.on_demand_enabled === true;
        });

        // Fetch audio stories from media_elements table
        // Looking for type='Audio' and status='Public'
        const { data: audioData, error: audioError } = await supabase
          .from('media_elements')
          .select('vc_id')
          .in('vc_id', artworkVcIds)
          .eq('type', 'Audio')
          .eq('status', 'Public');

        if (audioError) {
          console.warn('Error fetching audio stories:', audioError);
        }

        // Create a set of vc_ids that have public audio stories
        const audioStorySet = new Set((audioData || []).map(a => a.vc_id));
        
        // Process artwork data for our component's format
        const processedArtworks = artworksData.map(artwork => {
          const sceneElement = artworkElementsMap[artwork.vc_id] || {};
          return {
            vc_id: artwork.vc_id,
            title: artwork.title,
            width: artwork.width,
            height: artwork.height,
            depth: artwork.depth,
            price: artwork.price,
            status: artwork.status,
            audio_story: artwork.audio_story,
            cover_image: artwork.cover_image,
            uuid: artwork.uuid,
            nickname: artwork.artist_name,
            medium: artwork.medium,
            artwork_completed_date: artwork.artwork_completed_date,
            // Include scene element data
            parent_element_id: sceneElement.parent_element_id,
            wall_position_x: sceneElement.wall_position_x,
            wall_position_y: sceneElement.wall_position_y,
            element_id: sceneElement.element_id,
            display_location: sceneElement.text_content || '',
            // Print availability status
            prints_available: printStatusMap[artwork.vc_id] || false,
            // Has public audio story
            has_audio_story: audioStorySet.has(artwork.vc_id)
          };
        });
        
        setArtworks(processedArtworks);
        
        // Organize artworks by wall
        const artworkWallGroups = { null: [] }; // For artworks without a wall
        
        // Initialize all walls with empty arrays
        wallElements.forEach(wall => {
          artworkWallGroups[wall.element_id] = [];
        });
        
        // Group artworks by parent_element_id (wall)
        processedArtworks.forEach(artwork => {
          const wallId = artwork.parent_element_id;
          
          if (wallId && artworkWallGroups[wallId]) {
            artworkWallGroups[wallId].push(artwork);
          } else {
            artworkWallGroups.null.push(artwork);
          }
        });
        
        setArtworksByWall(artworkWallGroups);
      } catch (error) {
        console.error('Error fetching data:', error);
        setError(error.message || 'An error occurred while fetching data');
      } finally {
        setLoading(false);
      }
    };
    
    if (collectionId) {
      fetchData();
    }
  }, [collectionId]);

  // Update artwork display location
  const handleDisplayTextChange = (vc_id, value) => {
    setEditDisplayText(prev => ({
      ...prev,
      [vc_id]: value
    }));
  };

  // Save display location to the database
  const saveDisplayText = async (vc_id) => {
    try {
      setIsSaving(true);
      
      // Find artwork's scene element
      const artwork = artworks.find(a => a.vc_id === vc_id);
      if (!artwork || !artwork.element_id) {
        throw new Error('Artwork scene element not found');
      }
      
      // Update text_content in scene_elements table
      const { error } = await supabase
        .from('scene_elements')
        .update({ text_content: editDisplayText[vc_id] })
        .eq('element_id', artwork.element_id);
        
      if (error) throw error;
      
      // Update local state
      setArtworks(prev => prev.map(a => {
        if (a.vc_id === vc_id) {
          return { ...a, display_location: editDisplayText[vc_id] };
        }
        return a;
      }));
      
      // Update artworksByWall
      setArtworksByWall(prev => {
        const newGroups = { ...prev };
        
        Object.keys(newGroups).forEach(wallId => {
          newGroups[wallId] = newGroups[wallId].map(a => {
            if (a.vc_id === vc_id) {
              return { ...a, display_location: editDisplayText[vc_id] };
            }
            return a;
          });
        });
        
        return newGroups;
      });
      
      // Show success message
      const successMessage = document.createElement('div');
      successMessage.className = 'save-success-message';
      successMessage.textContent = 'Location saved!';
      
      // Find the appropriate save button and append the message next to it
      const saveButton = document.querySelector(`button[data-artwork-id="${vc_id}"]`);
      if (saveButton) {
        saveButton.parentNode.appendChild(successMessage);
        
        // Remove the message after 2 seconds
        setTimeout(() => {
          if (successMessage.parentNode) {
            successMessage.parentNode.removeChild(successMessage);
          }
        }, 2000);
      }
    } catch (error) {
      console.error('Error saving display text:', error);
      alert('Failed to save display text: ' + error.message);
    } finally {
      setIsSaving(false);
    }
  };

  // Get wall name from wall id
  const getWallName = (wallId) => {
    if (!wallId) return 'Unassigned';
    const wall = walls.find(w => w.element_id === wallId);
    return wall ? wall.source : `Wall ${wallId}`;
  };

  // Add a wall separator page to the PDF
  const addWallSeparatorPage = (doc, wallName, artworkCount) => {
    doc.addPage();
    
    // Center of the page
    const pageWidth = 8.5;
    const pageHeight = 11;
    const centerX = pageWidth / 2;
    const centerY = pageHeight / 2;
    
    // Add wall name in large text
    doc.setFont(undefined, 'bold');
    doc.setFontSize(36);
    doc.text(wallName, centerX, centerY - 1, { align: 'center' });
    
    // Add artwork count
    doc.setFont(undefined, 'normal');
    doc.setFontSize(18);
    doc.text(`${artworkCount} Artworks`, centerX, centerY, { align: 'center' });
    
    // Add instructions
    doc.setFontSize(14);
    doc.text('Place these labels on the corresponding wall', centerX, centerY + 1, { align: 'center' });
    
    return doc;
  };

  // Generate PDF when requested
  const generatePdf = async () => {
    try {
      setGenerating(true);
      
      // Create PDF document (8.5 x 11 inches, portrait)
      const doc = new jsPDF({
        orientation: 'portrait',
        unit: 'in',
        format: 'letter'
      });
      
      // Get template dimensions
      const template = AVERY_TEMPLATES[selectedTemplate];
      const cardWidth = template.cardWidth;
      const cardHeight = template.cardHeight;
      const margin = template.margin;
      const gap = template.gap;
      const cardsPerRow = template.cardsPerRow;
      const cardsPerCol = template.cardsPerCol;
      const cardsPerPage = cardsPerRow * cardsPerCol;
      
      // Create a flat array of all artworks for more efficient printing
      const allArtworks = [];
      
      // Get artworks by wall for PDF generation
      // First, get artworks with assigned walls (sorted by wall)
      walls.forEach(wall => {
        const wallArtworks = artworksByWall[wall.element_id] || [];
        if (wallArtworks.length === 0) return;
        
        // Sort artworks by position if needed
        const sortedWallArtworks = [...wallArtworks].sort((a, b) => {
          // Sort top to bottom, then left to right
          if (a.wall_position_y !== b.wall_position_y) {
            return a.wall_position_y - b.wall_position_y;
          }
          return a.wall_position_x - b.wall_position_x;
        });
        
        sortedWallArtworks.forEach(artwork => {
          allArtworks.push(artwork);
        });
      });
      
      // Then add unassigned artworks
      const unassignedArtworks = artworksByWall.null || [];
      unassignedArtworks.forEach(artwork => {
        allArtworks.push(artwork);
      });
      
      // Add QR codes for each artwork
      const qrPromises = allArtworks.map(async (artwork) => {
        try {
          const url = `https://share.vibrantcast.com/feelArtStory?id=${artwork.vc_id}&cid=${collectionId}&inperson=true`;
          const qrDataUrl = await QRCode.toDataURL(url, { width: 60, margin: 0 });
          return { vc_id: artwork.vc_id, qrDataUrl };
        } catch (err) {
          console.error(`Failed to generate QR code for artwork ${artwork.vc_id}:`, err);
          return { vc_id: artwork.vc_id, qrDataUrl: null };
        }
      });
      
  const qrCodes = await Promise.all(qrPromises);
  
  // Get template layout defaults
  const templateLayout = template.layout || {};
  
  // Ensure config has proper layout and visibility defaults
  const configWithDefaults = {
    ...currentTemplateConfig,
    layout: {
      ...templateLayout,  // Start with template defaults
      ...currentTemplateConfig?.layout,  // Override with user customizations
    },
    fieldVisibility: {
      showTitle: true,
      showArtist: true,
      showMedium: true,
      showCompletedDate: true,
      showDimensions: true,
      showPrice: true,
      showArtworkNumber: true,
      showQRCode: true,
      showCutLines: false,
      showPrintsAvailable: true,
      showAudioStory: true,
      ...currentTemplateConfig?.fieldVisibility,
    }
  };
  
  // Debug: Log the config being used for PDF generation
  console.log('PDF Generation - selectedTemplate:', selectedTemplate);
  console.log('PDF Generation - currentTemplateConfig:', currentTemplateConfig);
  console.log('PDF Generation - configWithDefaults.layout:', configWithDefaults.layout);
  console.log('PDF Generation - titleFontSize:', configWithDefaults.layout?.titleFontSize);
  
  const cutLinesEnabled = configWithDefaults.fieldVisibility?.showCutLines === true;
      
  // We'll use text to indicate audio presence
  const hasAudioText = "🎤";
      
      // Process each artwork, packing them efficiently onto pages
      for (let i = 0; i < allArtworks.length; i++) {
        const artwork = allArtworks[i];
        const pageIndex = Math.floor(i / cardsPerPage);
        const positionOnPage = i % cardsPerPage;
        const row = Math.floor(positionOnPage / cardsPerRow);
        const col = positionOnPage % cardsPerRow;
        
        // Add new page if needed
        if (positionOnPage === 0 && i > 0) {
          doc.addPage();
        }
        
        // Calculate card position
        const x = margin + col * (cardWidth + gap);
        const y = margin + row * (cardHeight + gap);
        
        // Draw faint cut lines when enabled
        if (cutLinesEnabled) {
          doc.setDrawColor(180);
          doc.setLineWidth(0.01);
          doc.rect(x, y, cardWidth, cardHeight);
          doc.setDrawColor(0);
        }
        
        // Find the QR code for this artwork
        const qrCode = qrCodes.find(qr => qr.vc_id === artwork.vc_id);
        
        // Use template-based layout with all available fields
        applyTemplateLayout(doc, x, y, cardWidth, cardHeight, artwork, qrCode, configWithDefaults);
      }
      
      // Convert to data URL and set state
      const dataUrl = doc.output('datauristring');
      setPdfDataUrl(dataUrl);
      
    } catch (error) {
      console.error('Failed to generate PDF:', error);
      setError('Failed to generate PDF: ' + error.message);
    } finally {
      setGenerating(false);
    }
  };

  // Generate individual PNG images for thermal printer at 203 DPI
  const generateThermalImages = async () => {
    try {
      setGeneratingImages(true);
      setThermalImages([]);

      // Get selected thermal template dimensions
      const thermalTemplate = AVERY_TEMPLATES[thermalTemplateSize];
      const widthInches = thermalTemplate.cardWidth;
      const heightInches = thermalTemplate.cardHeight;

      // 203 DPI is standard for thermal printers
      const DPI = 203;
      const widthPx = Math.round(widthInches * DPI);
      const heightPx = Math.round(heightInches * DPI);

      // Get the thermal template config - ensure QR code is enabled
      let thermalConfig = templateConfig.getConfig(thermalTemplateSize) || createDefaultConfig(thermalTemplate, thermalTemplateSize);
      // Ensure fieldVisibility defaults are set
      thermalConfig = {
        ...thermalConfig,
        fieldVisibility: {
          showTitle: true,
          showArtist: true,
          showMedium: true,
          showCompletedDate: true,
          showDimensions: true,
          showPrice: true,
          showArtworkNumber: true,
          showQRCode: true,
          showCutLines: false,
          showPrintsAvailable: true,
          showAudioStory: true,
          ...thermalConfig.fieldVisibility,
        }
      };

      // Prepare artworks list (same order as PDF generation) with wall info for subfolder grouping
      const allArtworks = [];
      walls.forEach(wall => {
        const wallArtworks = artworksByWall[wall.element_id] || [];
        const wallName = wall.name || `Wall ${wall.element_id}`;
        wallArtworks.forEach(artwork => allArtworks.push({ ...artwork, _wallName: wallName }));
      });
      const unassignedArtworks = artworksByWall.null || [];
      unassignedArtworks.forEach(artwork => allArtworks.push({ ...artwork, _wallName: 'Unassigned' }));

      // Set progress tracking
      setImageGenProgress({ current: 0, total: allArtworks.length });

      // Generate QR codes for all artworks
      const qrPromises = allArtworks.map(async (artwork) => {
        try {
          const url = `https://share.vibrantcast.com/feelArtStory?id=${artwork.vc_id}&cid=${collectionId}&inperson=true`;
          const qrDataUrl = await QRCode.toDataURL(url, { width: 200, margin: 0 });
          return { vc_id: artwork.vc_id, qrDataUrl };
        } catch (err) {
          console.error(`Failed to generate QR code for artwork ${artwork.vc_id}:`, err);
          return { vc_id: artwork.vc_id, qrDataUrl: null };
        }
      });

      const qrCodes = await Promise.all(qrPromises);
      const generatedImages = [];

      // Generate an image for each artwork
      for (let i = 0; i < allArtworks.length; i++) {
        const artwork = allArtworks[i];
        const qrCode = qrCodes.find(qr => qr.vc_id === artwork.vc_id);

        // Update progress
        setImageGenProgress({ current: i + 1, total: allArtworks.length });

        // Create a PDF document for this single label
        const doc = new jsPDF({
          orientation: 'portrait',
          unit: 'in',
          format: [widthInches, heightInches]
        });

        // Apply the thermal template layout
        applyTemplateLayout(doc, 0, 0, widthInches, heightInches, artwork, qrCode, thermalConfig);

        // Convert PDF to canvas then to PNG
        // Use jsPDF's output to get PDF data, then use PDF.js to render to canvas
        const pdfBlob = doc.output('blob');
        const pdfUrl = URL.createObjectURL(pdfBlob);

        try {
          // Load the PDF and render to canvas
          const loadingTask = pdfjs.getDocument(pdfUrl);
          const pdf = await loadingTask.promise;
          const page = await pdf.getPage(1);

          // Calculate scale for 203 DPI output
          const viewport = page.getViewport({ scale: 1 });
          const scale = widthPx / viewport.width;
          const scaledViewport = page.getViewport({ scale });

          // Create canvas and render
          const canvas = document.createElement('canvas');
          canvas.width = widthPx;
          canvas.height = heightPx;
          const context = canvas.getContext('2d');

          // Fill with white background
          context.fillStyle = '#ffffff';
          context.fillRect(0, 0, widthPx, heightPx);

          await page.render({
            canvasContext: context,
            viewport: scaledViewport
          }).promise;

          // Draw QR code directly on canvas (bypasses PDF.js image rendering issues)
          // PDF.js sometimes fails to render embedded images from jsPDF
          const showQR = thermalConfig.fieldVisibility?.showQRCode !== false;
          if (showQR && qrCode && qrCode.qrDataUrl) {
            try {
              // Load QR code image
              const qrImg = new Image();
              await new Promise((resolve, reject) => {
                qrImg.onload = resolve;
                qrImg.onerror = reject;
                qrImg.src = qrCode.qrDataUrl;
              });

              // Calculate position in pixels based on layout config
              const layout = thermalConfig.layout || {};
              const qrX = (layout.qrCodeX || 2.15) * DPI;
              const qrY = (layout.qrCodeY || 0.2) * DPI;
              const qrSize = (layout.qrCodeSize || 0.7) * DPI;

              // Draw white background for QR code area (to cover any PDF artifacts)
              context.fillStyle = '#ffffff';
              context.fillRect(qrX - 2, qrY - 2, qrSize + 4, qrSize + 4);

              // Draw QR code
              context.drawImage(qrImg, qrX, qrY, qrSize, qrSize);
            } catch (qrDrawError) {
              console.error('Error drawing QR code on canvas:', qrDrawError);
            }
          }

          // Convert to PNG data URL
          const imageDataUrl = canvas.toDataURL('image/png');

          // Create filename with wall prefix and NFS prefix if not for sale
          const statusLower = (artwork.status || '').toLowerCase();
          const isNFS = statusLower === 'not for sale' || statusLower === 'sold' || (!artwork.price && statusLower !== 'for sale');
          const nfsPrefix = isNFS ? 'NFS-' : '';
          const wallPrefix = (artwork._wallName || 'Unassigned').replace(/[^a-z0-9]/gi, '_');
          const titleSlug = (artwork.title || 'untitled').replace(/[^a-z0-9]/gi, '_').substring(0, 20);
          
          generatedImages.push({
            vc_id: artwork.vc_id,
            title: artwork.title || 'Untitled',
            artist: artwork.nickname || 'Unknown',
            imageDataUrl,
            filename: `${nfsPrefix}${wallPrefix}-label-${artwork.vc_id}-${titleSlug}.png`
          });

          // Clean up
          URL.revokeObjectURL(pdfUrl);
        } catch (renderError) {
          console.error(`Failed to render image for artwork ${artwork.vc_id}:`, renderError);
          URL.revokeObjectURL(pdfUrl);
        }
      }

      setThermalImages(generatedImages);
      setShowThermalExport(true);

    } catch (error) {
      console.error('Failed to generate thermal images:', error);
      setError('Failed to generate thermal images: ' + error.message);
    } finally {
      setGeneratingImages(false);
    }
  };

  // Download all thermal images as a zip file
  const downloadAllThermalImages = async () => {
    if (thermalImages.length === 0) return;

    // For single image, just download directly
    if (thermalImages.length === 1) {
      const link = document.createElement('a');
      link.href = thermalImages[0].imageDataUrl;
      link.download = thermalImages[0].filename;
      link.click();
      return;
    }

    // Create a ZIP file containing all images
    const zip = new JSZip();
    const imgFolder = zip.folder('thermal-labels');

    // Add each image to the ZIP
    for (const img of thermalImages) {
      // Convert data URL to blob data
      const base64Data = img.imageDataUrl.split(',')[1];
      imgFolder.file(img.filename, base64Data, { base64: true });
    }

    // Generate and download the ZIP file
    const zipBlob = await zip.generateAsync({ type: 'blob' });
    const zipUrl = URL.createObjectURL(zipBlob);
    const link = document.createElement('a');
    link.href = zipUrl;
    link.download = `thermal-labels-${collectionId}-${thermalImages.length}images.zip`;
    link.click();
    URL.revokeObjectURL(zipUrl);
  };

  // Download single thermal image
  const downloadThermalImage = (image) => {
    const link = document.createElement('a');
    link.href = image.imageDataUrl;
    link.download = image.filename;
    link.click();
  };

  // Standard layout with QR code
  // Layout function using template specifications
  const applyTemplateLayout = (doc, x, y, cardWidth, cardHeight, artwork, qrCode, config) => {
    if (!config || !config.layout || !config.fieldVisibility) {
      console.error("Incomplete configuration provided to applyTemplateLayout", config);
      return; // Exit if config is not valid
    }
    const layout = config.layout; // Use config.layout for positions and font sizes
    const visibility = config.fieldVisibility; // Use config.fieldVisibility

    const toNumber = (value, fallback) => {
      const numericValue = typeof value === 'number' ? value : parseFloat(value);
      return Number.isFinite(numericValue) ? numericValue : fallback;
    };

    const defaultStartX = toNumber(layout.contentStartX, 0.1);
    const defaultEndX = toNumber(layout.contentEndX, cardWidth - 0.1);
    const defaultTitleY = toNumber(layout.titleY, 0.2);
    const defaultArtistY = toNumber(layout.artistY, defaultTitleY + 0.17);
    const defaultMediumY = toNumber(layout.mediumY, defaultArtistY + 0.15);
    const defaultCompletedDateY = toNumber(layout.completedDateY ?? layout.yearY, defaultMediumY + 0.1);
    const defaultDimensionsY = toNumber(layout.dimensionsY, defaultCompletedDateY + 0.15);
    const defaultPriceY = toNumber(layout.priceY, cardHeight - 0.2);

    const safeLayout = {
      titleFontSize: toNumber(layout.titleFontSize, 12),
      titleX: toNumber(layout.titleX, defaultStartX),
      titleY: defaultTitleY,

      artistFontSize: toNumber(layout.artistFontSize, 10),
      artistX: toNumber(layout.artistX, defaultStartX),
      artistY: defaultArtistY,

      infoFontSize: toNumber(layout.infoFontSize, 10),

      mediumX: toNumber(layout.mediumX, defaultStartX),
      mediumY: defaultMediumY,

      completedDateX: toNumber(layout.completedDateX, defaultStartX),
      completedDateY: defaultCompletedDateY,

      dimensionsX: toNumber(layout.dimensionsX, defaultStartX),
      dimensionsY: defaultDimensionsY,

      priceFontSize: toNumber(layout.priceFontSize, toNumber(layout.infoFontSize, 10)),
      priceX: toNumber(layout.priceX, defaultStartX),
      priceY: defaultPriceY,

      contentStartX: defaultStartX,
      contentEndX: defaultEndX,

      qrCodeX: Math.min(toNumber(layout.qrCodeX, cardWidth - 0.7), cardWidth - 0.3),
      qrCodeY: Math.min(toNumber(layout.qrCodeY, 0.15), cardHeight - 0.3),
      qrCodeSize: toNumber(layout.qrCodeSize, 0.5),

      artworkNumberX: toNumber(layout.artworkNumberX, defaultStartX),
      artworkNumberY: toNumber(layout.artworkNumberY, cardHeight - 0.2),
      artworkNumberFontSize: toNumber(layout.artworkNumberFontSize, 8),

      printsAvailableX: toNumber(layout.printsAvailableX, cardWidth - 1.2),
      printsAvailableY: toNumber(layout.printsAvailableY, cardHeight - 0.2),
      printsAvailableFontSize: toNumber(layout.printsAvailableFontSize, 9),

      audioIconX: toNumber(layout.audioIconX, cardWidth - 0.35),
      audioIconY: toNumber(layout.audioIconY, 0.15),
      audioIconSize: toNumber(layout.audioIconSize, 0.2),
    };

    if (safeLayout.contentEndX <= safeLayout.contentStartX) {
      safeLayout.contentEndX = safeLayout.contentStartX + Math.max(0.5, cardWidth * 0.5);
    }

    const titleFitMode = titleFitModeState || 'wrap';
    const getFieldWidth = (fieldX, useFullWidth = false) => {
      const EDGE_MARGIN = 0.1;
      // Use full card width for title and medium fields
      if (useFullWidth || ((titleFitMode === 'wrap' || titleFitMode === 'single-line-auto') && fieldX === safeLayout.titleX)) {
        return Math.max(0.1, cardWidth - fieldX - EDGE_MARGIN);
      }
      return Math.max(0.1, safeLayout.contentEndX - fieldX);
    };
    
    // Helper function to set font style (must be defined before other helpers that use it)
    const setFontStyle = (fontStyle, fontSize) => {
      if (fontStyle === 'bold') {
        doc.setFont(undefined, 'bold');
      } else if (fontStyle === 'italic') {
        doc.setFont(undefined, 'italic');
      } else {
        doc.setFont(undefined, 'normal');
      }
      doc.setFontSize(toNumber(fontSize, 10));
    };
    
    // Helper: Add text with up to 2 lines, wrapping at word boundaries
    // Uses full card width to ensure medium descriptions aren't overly constrained
    const addTextWrapped2Lines = (text, startX, startY, maxWidth, fontStyle, fontSize) => {
      if (!text) return;
      
      setFontStyle(fontStyle, fontSize);
      const effectiveSize = toNumber(fontSize, 10);
      const lineHeight = effectiveSize * 1.2 / 72; // Convert to inches (1.2 line spacing)
      
      // Always use generous width: full card width minus left margin and small right margin
      // This ignores the QR code constraint for medium text
      const leftMargin = startX - x; // How far from card edge we start
      const availableWidth = cardWidth - leftMargin - 0.1; // Leave 0.1" right margin
      
      // Use doc.splitTextToSize for reliable text wrapping
      const lines = doc.splitTextToSize(text, availableWidth);
      
      // Draw up to 2 lines
      if (lines.length === 1) {
        doc.text(lines[0], startX, startY);
      } else if (lines.length >= 2) {
        doc.text(lines[0], startX, startY);
        // For second line, truncate with ellipsis if there are more lines
        if (lines.length > 2) {
          const line2 = lines[1].trim();
          // Truncate and add ellipsis
          let truncated = line2;
          while (truncated.length > 0) {
            const testWidth = doc.getStringUnitWidth(truncated + '...') * effectiveSize / doc.internal.scaleFactor;
            if (testWidth <= availableWidth) break;
            truncated = truncated.slice(0, -1);
          }
          doc.text(truncated + '...', startX, startY + lineHeight);
        } else {
          doc.text(lines[1], startX, startY + lineHeight);
        }
      }
    };
    
    // Helper: Add text with ellipsis if too long (single line)
    const addTextTruncated = (text, startX, startY, maxWidth, fontStyle, fontSize) => {
      if (!text) return;
      
      setFontStyle(fontStyle, fontSize);
      const effectiveSize = toNumber(fontSize, 10);
      const textWidth = doc.getStringUnitWidth(text) * effectiveSize / doc.internal.scaleFactor;
      
      if (textWidth > maxWidth) {
        let truncated = text;
        while (truncated.length > 0) {
          const testWidth = doc.getStringUnitWidth(truncated + '...') * effectiveSize / doc.internal.scaleFactor;
          if (testWidth <= maxWidth) break;
          truncated = truncated.slice(0, -1);
        }
        doc.text(truncated + '...', startX, startY);
      } else {
        doc.text(text, startX, startY);
      }
    };
    
    // Helper: Split text into lines with max width constraint
    const splitIntoLines = (text, maxWidth, fontSize) => {
      const words = text.split(' ');
      const lines = [];
      let currentLine = '';
      const effectiveSize = toNumber(fontSize, 10);
      
      words.forEach(word => {
        const testLine = currentLine ? currentLine + ' ' + word : word;
        const lineWidth = doc.getStringUnitWidth(testLine) * effectiveSize / doc.internal.scaleFactor;
        
        if (lineWidth <= maxWidth) {
          currentLine = testLine;
        } else {
          if (currentLine) lines.push(currentLine);
          currentLine = word;
        }
      });
      
      if (currentLine) lines.push(currentLine);
      return lines;
    };
    
    // Line height multiplier: 1.15 is standard (15% above font size)
    const getLineHeight = (fontSize) => {
      const effectiveSize = toNumber(fontSize, 10);
      return effectiveSize * 1.15 / 72; // Convert to inches
    };
    
    // Helper: Add title with smart sizing/wrapping - RETURNS actual line count and height
    const addSmartTitle = (text, startX, startY, maxWidth, fontStyle, originalFontSize) => {
      const baseFontSize = toNumber(originalFontSize, 12);
      if (!text) return { lineCount: 1, height: getLineHeight(baseFontSize) };
      
      let actualFontSize = baseFontSize;
      let usedLines = 1;
      const lineHeight = getLineHeight(baseFontSize);
      
      setFontStyle(fontStyle, baseFontSize);
      const lines = splitIntoLines(text, maxWidth, baseFontSize);
      
      if (titleFitMode === 'ellipsis') {
        // Single line with ellipsis
        addTextTruncated(text, startX, startY, maxWidth, fontStyle, baseFontSize);
        usedLines = 1;
      } else if (titleFitMode === 'single-line-auto') {
        // Force single line and shrink font until it fits the available width
        const MIN_FONT_RATIO = 0.5;
        let autoFontSize = baseFontSize;
        setFontStyle(fontStyle, autoFontSize);
        let textWidth = doc.getStringUnitWidth(text) * autoFontSize / doc.internal.scaleFactor;

        while (textWidth > maxWidth && autoFontSize > baseFontSize * MIN_FONT_RATIO) {
          autoFontSize = Math.max(baseFontSize * MIN_FONT_RATIO, autoFontSize - 0.5);
          setFontStyle(fontStyle, autoFontSize);
          textWidth = doc.getStringUnitWidth(text) * autoFontSize / doc.internal.scaleFactor;
        }

        doc.text(text, startX, startY);
        actualFontSize = autoFontSize;
        usedLines = 1;
      } else if (titleFitMode === 'wrap') {
        // Allow up to 2 lines; if 3+ lines, shrink font and retry
        if (lines.length <= 2) {
          lines.forEach((line, idx) => {
            doc.text(line, startX, startY + (idx * lineHeight));
          });
          usedLines = lines.length;
        } else {
          // Too many lines - reduce font size and try again
          actualFontSize = baseFontSize * 0.85;
          const reducedLineHeight = getLineHeight(actualFontSize);
          setFontStyle(fontStyle, actualFontSize);
          const linesReduced = splitIntoLines(text, maxWidth, actualFontSize);
          linesReduced.slice(0, 2).forEach((line, idx) => {
            doc.text(line, startX, startY + (idx * reducedLineHeight));
          });
          usedLines = Math.min(linesReduced.length, 2);
          // If still too long, add ellipsis to 2nd line
          if (linesReduced.length > 2) {
            setFontStyle(fontStyle, actualFontSize);
            const lastLine = linesReduced[1];
            const truncated = lastLine.slice(0, -3) + '...';
            doc.text(truncated, startX, startY + reducedLineHeight);
          }
        }
      } else if (titleFitMode === 'shrink') {
        // Automatically reduce font if needed to fit 2 lines
        let useFontSize = baseFontSize;
        let useLines = lines;
        let usedLineHeight = lineHeight;
        
        while (useLines.length > 2 && useFontSize > baseFontSize * 0.6) {
          useFontSize *= 0.9;
          usedLineHeight = getLineHeight(useFontSize);
          setFontStyle(fontStyle, useFontSize);
          useLines = splitIntoLines(text, maxWidth, useFontSize);
        }
        
        useLines.slice(0, 2).forEach((line, idx) => {
          doc.text(line, startX, startY + (idx * usedLineHeight));
        });
        actualFontSize = useFontSize;
        usedLines = useLines.length > 2 ? 2 : useLines.length;
      }
      
      return { lineCount: usedLines, height: getLineHeight(actualFontSize) };
    };
    
    // ========== ABSOLUTE FIELD POSITIONS ==========

    // Title (if enabled)
    if (visibility.showTitle !== false) {
      addSmartTitle(
        artwork.title || 'Untitled', 
        x + safeLayout.titleX,
        y + safeLayout.titleY,
        getFieldWidth(safeLayout.titleX),
        'bold',
        safeLayout.titleFontSize
      );
    }

    // Artist (if enabled)
    if (visibility.showArtist !== false) {
      addTextTruncated(
        artwork.nickname || 'Unknown Artist', 
        x + safeLayout.artistX,
        y + safeLayout.artistY,
        getFieldWidth(safeLayout.artistX),
        'italic',
        safeLayout.artistFontSize
      );
    }

    if (visibility.showMedium !== false && artwork && artwork.medium) {
      addTextWrapped2Lines(
        artwork.medium,
        x + safeLayout.mediumX,
        y + safeLayout.mediumY,
        getFieldWidth(safeLayout.mediumX, true), // Use full width for medium
        'normal',
        safeLayout.infoFontSize
      );
    }

    if (visibility.showCompletedDate !== false && artwork && artwork.artwork_completed_date) {
      setFontStyle('normal', safeLayout.infoFontSize);
      const year = new Date(artwork.artwork_completed_date).getFullYear();
      doc.text(`${year}`, x + safeLayout.completedDateX, y + safeLayout.completedDateY);
    }

    // Dimensions (if enabled)
    if (visibility.showDimensions !== false && (artwork.width || artwork.height)) {
      addTextTruncated(
        `${artwork.width || 0}"w x ${artwork.height || 0}"h`,
        x + safeLayout.dimensionsX,
        y + safeLayout.dimensionsY,
        getFieldWidth(safeLayout.dimensionsX),
        'normal',
        safeLayout.infoFontSize
      );
    }

    if (visibility.showPrice !== false) {
      setFontStyle('bold', safeLayout.priceFontSize);
      const statusLower = (artwork.status || '').toLowerCase();
      const isNFS = statusLower === 'not for sale' || statusLower === 'sold' || (!artwork.price && statusLower !== 'for sale');
      const priceText = isNFS ? 'NFS' : `$${artwork.price}`;
      doc.text(priceText, x + safeLayout.priceX, y + safeLayout.priceY);
    }

    // Artwork Number (if enabled)
    if (visibility.showArtworkNumber !== false && artwork.vc_id) {
  setFontStyle('normal', safeLayout.artworkNumberFontSize); // Small font size
    doc.text(`${artwork.vc_id}`, x + safeLayout.artworkNumberX, y + safeLayout.artworkNumberY);
    }

    // Prints Available indicator (if enabled and artwork has prints)
    if (visibility.showPrintsAvailable !== false && artwork.prints_available) {
      setFontStyle('bold', safeLayout.printsAvailableFontSize);
      doc.setTextColor(22, 101, 52); // Dark green color (#166534)
      doc.text('Prints Available', x + safeLayout.printsAvailableX, y + safeLayout.printsAvailableY);
      doc.setTextColor(0); // Reset to black
    }

    // Audio Story indicator (microphone icon) - if enabled and artwork has audio
    if (visibility.showAudioStory !== false && artwork.has_audio_story) {
      const iconX = x + safeLayout.audioIconX;
      const iconY = y + safeLayout.audioIconY;
      const iconHeight = safeLayout.audioIconSize; // Already in inches - use as height
      
      try {
        // Create a temporary image to get natural dimensions for aspect ratio
        const img = new Image();
        img.src = micIcon;
        // Use a reasonable aspect ratio for microphone icon (typically taller than wide)
        // Most mic icons are roughly 0.7:1 width:height ratio
        const aspectRatio = img.naturalWidth && img.naturalHeight ? img.naturalWidth / img.naturalHeight : 0.7;
        const iconWidth = iconHeight * aspectRatio;
        
        doc.addImage(micIcon, 'PNG', iconX, iconY, iconWidth, iconHeight);
      } catch (err) {
        console.error('Error adding mic icon:', err);
        // Fallback: draw a simple circle with "A" for Audio
        doc.setFillColor(79, 70, 229);
        doc.circle(iconX + iconHeight/2, iconY + iconHeight/2, iconHeight/2, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(iconHeight * 50);
        doc.text('A', iconX + iconHeight * 0.3, iconY + iconHeight * 0.7);
        doc.setTextColor(0);
        doc.setFillColor(0);
      }
    }
    
    // QR Code (if enabled) - default to true if not explicitly set
    const showQR = visibility.showQRCode !== false;
    if (showQR) {
      const qrX = x + safeLayout.qrCodeX;
      const qrY = y + safeLayout.qrCodeY;
      const qrSize = safeLayout.qrCodeSize;
      
      // Always draw a border to show QR position
      doc.setDrawColor(200);
      doc.setLineWidth(0.02);
      doc.rect(qrX, qrY, qrSize, qrSize);
      doc.setDrawColor(0);
      
      if (qrCode && qrCode.qrDataUrl) {
        try {
          doc.addImage(qrCode.qrDataUrl, 'PNG', qrX, qrY, qrSize, qrSize);
        } catch (err) {
          console.error('Error adding QR code:', err);
          // Draw "QR" text on error
          doc.setFontSize(8);
          doc.setFont(undefined, 'normal');
          doc.text('QR', qrX + qrSize/2 - 0.08, qrY + qrSize/2 + 0.03);
        }
      } else {
        // Draw "QR" text when no QR data available
        doc.setFontSize(8);
        doc.setFont(undefined, 'normal');
        doc.setTextColor(150);
        doc.text('QR', qrX + qrSize/2 - 0.08, qrY + qrSize/2 + 0.03);
        doc.setTextColor(0);
      }
    }
  };

  const applyStandardLayout = (doc, x, y, cardWidth, cardHeight, artwork, qrCode) => {
    // Add artist name
    doc.setFont(undefined, 'normal');
    doc.setFontSize(artistFontSize);
    doc.text(artwork.nickname || 'Unknown Artist', x + 0.2, y + 0.3);
    
    // Add artwork title (bold) with better handling for long titles
    doc.setFont(undefined, 'bold');
    doc.setFontSize(titleFontSize);
    let title = artwork.title || 'Untitled';
    
    // Handle long titles - support up to 2 lines
    const maxTitleWidth = cardWidth - 0.4;
    const titleWidth = doc.getStringUnitWidth(title) * titleFontSize / doc.internal.scaleFactor;
    
    if (titleWidth > maxTitleWidth) {
      // Title is too wide for one line, need to split
      const words = title.split(' ');
      let line1 = '';
      let line2 = '';
      let testLine = '';
      
      // Try to fit as many words as possible on first line
      for (let i = 0; i < words.length; i++) {
        testLine = line1 + (line1 ? ' ' : '') + words[i];
        const testWidth = doc.getStringUnitWidth(testLine) * titleFontSize / doc.internal.scaleFactor;
        
        if (testWidth <= maxTitleWidth) {
          line1 = testLine;
        } else {
          // Start second line
          line2 = words.slice(i).join(' ');
          break;
        }
      }
      
      // Check if second line is too long
      const line2Width = doc.getStringUnitWidth(line2) * titleFontSize / doc.internal.scaleFactor;
      if (line2Width > maxTitleWidth) {
        // Truncate and add ellipsis
        let truncatedLine2 = '';
        for (let i = 0; i < line2.length; i++) {
          const testLine = truncatedLine2 + line2[i];
          const testWidth = doc.getStringUnitWidth(testLine + '...') * titleFontSize / doc.internal.scaleFactor;
          
          if (testWidth <= maxTitleWidth) {
            truncatedLine2 = testLine;
          } else {
            break;
          }
        }
        line2 = truncatedLine2 + '...';
      }
      
      // Draw the title on two lines
      doc.text(line1, x + 0.2, y + 0.6);
      doc.text(line2, x + 0.2, y + 0.6 + (titleFontSize/72));
    } else {
      // Title fits on one line
      doc.text(title, x + 0.2, y + 0.6);
    }
    
    // Add dimensions
    doc.setFont(undefined, 'normal');
    doc.setFontSize(infoFontSize);
    const dimensions = `${artwork.width || 0} w x ${artwork.height || 0} h`;
    doc.text(dimensions, x + 0.2, y + cardHeight - 0.8);
    
    // Add display location if available (user-provided location text)
    if (editDisplayText[artwork.vc_id]) {
      doc.setFont(undefined, 'normal');
      doc.setFontSize(infoFontSize - 1);
      doc.text(editDisplayText[artwork.vc_id], x + cardWidth - 1.0, y + cardHeight - 1.4);
    }
    
    // Add artwork ID (small text) - Adjusted for QR code
    doc.setFontSize(8);
    doc.text(`${artwork.vc_id}`, x + cardWidth - 1.0, y + cardHeight - 1.2);
    
    // Add price if enabled and valid
    if (showPrice) {
      doc.setFont(undefined, 'bold');
      doc.setFontSize(priceFontSize);
      const statusLower = (artwork.status || '').toLowerCase();
      const isNFS = statusLower === 'not for sale' || statusLower === 'sold' || (!artwork.price && statusLower !== 'for sale');
      const price = isNFS ? 'NFS' : `$${artwork.price}`;
      doc.text(price, x + 0.2, y + cardHeight - 0.2);
    }
    
    // Add QR code - size depends on card size
    const qrSize = cardHeight === 2.0 ? 0.7 : 0.9; // Smaller QR for business card
    if (qrCode && qrCode.qrDataUrl) {
      doc.addImage(
        qrCode.qrDataUrl, 
        'PNG', 
        x + cardWidth - qrSize - 0.1, 
        y + cardHeight - qrSize - 0.1, 
        qrSize,
        qrSize
      );
    }
    
    // Add microphone icon if artwork has audio story
    if (artwork.audio_story) {
      doc.setFont(undefined, 'normal');
      doc.setFontSize(infoFontSize);
      doc.text("🎤", x + cardWidth - 1.4, y + cardHeight - 0.4);
    }
  };

  // Clear PDF preview
  const clearPreview = () => {
    setPdfDataUrl(null);
    // Restore normal scrolling
    document.body.style.overflow = 'auto';
  };

  // Set body overflow when showing PDF
  useEffect(() => {
    if (pdfDataUrl) {
      // Prevent background scrolling when PDF is open
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
    
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [pdfDataUrl]);

  return (
    <div className="pdf-generator-wrapper">
      <div className="pdf-generator-container">
        <div className="pdf-generator-header">
          <h1>Artwork Label Generator</h1>
          <div className="header-actions">
            <button 
              onClick={() => navigate(-1)}
              className="back-link"
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: '#4F46E5',
                fontSize: '16px',
                padding: '8px 0',
                textDecoration: 'none',
                display: 'flex',
                alignItems: 'center',
                gap: '4px'
              }}
            >
              <ChevronLeft size={20} />
              Back
            </button>
          </div>
        </div>
        
        {loading ? (
          <div className="loading-indicator">
            <div className="spinner"></div>
            <p>Loading artwork data...</p>
          </div>
        ) : error ? (
          <div className="error-message">
            <h3>Error</h3>
            <p>{error}</p>
            <Link to={`/admin/collections/${collectionId}`} className="button primary-button">
              Return to Collection
            </Link>
          </div>
        ) : (
          <div className="pdf-generator-content">
            <div className="collection-info-card">
              <h2>{collection?.title || 'Collection'}</h2>
              <p>{artworks.length} artworks found in {walls.length} wall sections</p>
              
              <div className="control-options">
              {/* Template Selection with Preview and Options */}
              {currentTemplateConfig && artworks.length > 0 && (
                <div className="template-and-options-wrapper">
                  {/* Template Controls with Live Preview - New Unified Panel */}
                  <TemplateControlsPanel
                    selectedTemplate={selectedTemplate}
                    AVERY_TEMPLATES={AVERY_TEMPLATES}
                    onTemplateChange={setSelectedTemplate}
                    previewImageUrl={previewImageUrl}
                    config={currentTemplateConfig}
                    onConfigChange={(newConfig) => {
                      console.log('onConfigChange called with:', newConfig?.layout?.titleFontSize);
                      setCurrentTemplateConfig(newConfig);
                      // Auto-save to localStorage on every change
                      const saved = templateConfig.saveConfig(selectedTemplate, newConfig);
                      console.log('saveConfig result:', saved);
                    }}
                    titleFitMode={titleFitModeState}
                    onTitleFitModeChange={setTitleFitModeState}
                    artworks={artworks}
                    currentArtworkIndex={previewArtworkIndex}
                    onArtworkChange={setPreviewArtworkIndex}
                  />
                </div>
              )}

              {/* Generate Buttons */}
              {currentTemplateConfig && artworks.length > 0 && (
                <div className="generate-buttons-row" style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                  <button
                    className="button primary-button generate-button"
                    onClick={generatePdf}
                    disabled={generating || artworks.length === 0}
                  >
                    {generating ? 'Generating...' : 'Generate PDF Labels'}
                  </button>
                  {/* Only show thermal export when a thermal template is selected */}
                  {AVERY_TEMPLATES[selectedTemplate]?.isThermal && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <select
                        value={thermalTemplateSize}
                        onChange={(e) => setThermalTemplateSize(e.target.value)}
                        disabled={generatingImages}
                        style={{
                          padding: '10px 12px',
                          borderRadius: '6px',
                          border: '1px solid #d1d5db',
                          fontSize: '14px',
                          cursor: 'pointer'
                        }}
                      >
                        <option value="thermal_3x2">3×2 inch</option>
                        <option value="thermal_4x6">4×6 inch</option>
                      </select>
                      <button
                        className="button secondary-button generate-button"
                        onClick={generateThermalImages}
                        disabled={generatingImages || artworks.length === 0}
                        style={{
                          background: '#059669',
                          color: 'white',
                          border: 'none',
                          minWidth: '220px'
                        }}
                        title="Generate PNG images for Bluetooth thermal printers (JADENS, etc.)"
                      >
                        {generatingImages
                          ? `Generating ${imageGenProgress.current}/${imageGenProgress.total}...`
                          : 'Export for Thermal Printer'}
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
            </div>

            {/* Thermal Export Modal */}
            {showThermalExport && thermalImages.length > 0 && (
              <>
                <div
                  className="drawer-overlay"
                  onClick={() => setShowThermalExport(false)}
                  style={{ zIndex: 1000 }}
                />
                <div style={{
                  position: 'fixed',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  background: 'white',
                  borderRadius: '12px',
                  boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                  zIndex: 1001,
                  maxWidth: '900px',
                  width: '90%',
                  maxHeight: '85vh',
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column'
                }}>
                  <div style={{
                    padding: '20px 24px',
                    borderBottom: '1px solid #e5e7eb',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <div>
                      <h2 style={{ margin: 0, fontSize: '20px', color: '#1f2937' }}>
                        Thermal Printer Export
                      </h2>
                      <p style={{ margin: '4px 0 0', color: '#6b7280', fontSize: '14px' }}>
                        {thermalImages.length} label images ({AVERY_TEMPLATES[thermalTemplateSize]?.cardWidth}×{AVERY_TEMPLATES[thermalTemplateSize]?.cardHeight}" at 203 DPI) ready for JADENS or similar printers
                      </p>
                    </div>
                    <button
                      onClick={() => setShowThermalExport(false)}
                      style={{
                        background: 'none',
                        border: 'none',
                        fontSize: '24px',
                        cursor: 'pointer',
                        color: '#6b7280',
                        padding: '4px 8px'
                      }}
                    >
                      ×
                    </button>
                  </div>

                  <div style={{
                    padding: '16px 24px',
                    background: '#f0fdf4',
                    borderBottom: '1px solid #e5e7eb'
                  }}>
                    <p style={{ margin: 0, fontSize: '14px', color: '#166534' }}>
                      <strong>How to use:</strong> Download the images below and open them in the JadensPrinter app
                      (or your thermal printer's app) to print via Bluetooth.
                    </p>
                  </div>

                  <div style={{
                    padding: '20px 24px',
                    display: 'flex',
                    gap: '12px',
                    borderBottom: '1px solid #e5e7eb'
                  }}>
                    <button
                      onClick={downloadAllThermalImages}
                      style={{
                        background: '#059669',
                        color: 'white',
                        border: 'none',
                        padding: '10px 20px',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontWeight: '600',
                        fontSize: '14px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}
                    >
                      <Download size={18} />
                      {thermalImages.length === 1
                        ? 'Download Image'
                        : `Download ZIP (${thermalImages.length} images)`}
                    </button>
                  </div>

                  <div style={{
                    flex: 1,
                    overflowY: 'auto',
                    padding: '20px 24px'
                  }}>
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                      gap: '16px'
                    }}>
                      {thermalImages.map((img) => (
                        <div key={img.vc_id} style={{
                          border: '1px solid #e5e7eb',
                          borderRadius: '8px',
                          overflow: 'hidden',
                          background: '#f9fafb'
                        }}>
                          <div style={{
                            aspectRatio: '4/6',
                            background: '#fff',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            borderBottom: '1px solid #e5e7eb'
                          }}>
                            <img
                              src={img.imageDataUrl}
                              alt={`Label for ${img.title}`}
                              style={{
                                maxWidth: '100%',
                                maxHeight: '100%',
                                objectFit: 'contain'
                              }}
                            />
                          </div>
                          <div style={{ padding: '12px' }}>
                            <p style={{
                              margin: '0 0 4px',
                              fontWeight: '600',
                              fontSize: '13px',
                              color: '#1f2937',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis'
                            }}>
                              {img.title}
                            </p>
                            <p style={{
                              margin: '0 0 8px',
                              fontSize: '12px',
                              color: '#6b7280'
                            }}>
                              {img.artist} • #{img.vc_id}
                            </p>
                            <button
                              onClick={() => downloadThermalImage(img)}
                              style={{
                                width: '100%',
                                background: '#f3f4f6',
                                border: '1px solid #d1d5db',
                                padding: '6px 12px',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '12px',
                                color: '#374151'
                              }}
                            >
                              Download PNG
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </>
            )}

            {pdfDataUrl ? (
              <div className="pdf-preview-container fullwidth">
                <div className="pdf-preview-header" style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  marginBottom: '20px',
                  paddingBottom: '20px',
                  borderBottom: '1px solid #dee2e6'
                }}>
                  <div>
                    <h2 style={{ margin: '0 0 10px 0', color: '#1f2937' }}>Display Labels</h2>
                    <p style={{ margin: '0', color: '#6b7280', fontSize: '16px' }}>
                      {artworks.length} labels created across {walls.length} wall sections
                    </p>
                  </div>
                  
                  <div className="preview-actions" style={{
                    display: 'flex',
                    gap: '12px',
                    flexDirection: 'column',
                    alignItems: 'flex-end'
                  }}>
                    <a
                      href={pdfDataUrl}
                      download={`artwork-labels-${collectionId}.pdf`}
                      className="button primary-button"
                      style={{ 
                        textDecoration: 'none',
                        minWidth: '160px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '6px'
                      }}
                    >
                      <Download size={18} />
                      Download PDF
                    </a>
                    <button
                      className="button text-button"
                      onClick={clearPreview}
                      style={{
                        minWidth: '160px'
                      }}
                    >
                      Close Preview
                    </button>
                  </div>
                </div>
                  
                {/* PDF Viewer Content Wrapper */}
                <div className="pdf-preview-content-wrapper">
                {/* PDF Viewer */}
                <div style={{
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  overflow: 'hidden',
                  background: '#f5f5f5',
                  display: 'flex',
                  flexDirection: 'column',
                  maxHeight: 'calc(100vh - 250px)'
                }}>
                    {/* Zoom and Print Controls */}
                    <div style={{
                      display: 'flex',
                      gap: '15px',
                      padding: '15px',
                      alignItems: 'center',
                      flexWrap: 'wrap',
                      justifyContent: 'center',
                      backgroundColor: '#fff',
                      borderBottom: '1px solid #ddd',
                      width: '100%'
                    }}>
                      {/* Zoom Controls */}
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <button
                          onClick={() => setScale(Math.max(0.5, scale - 0.2))}
                          style={{
                            padding: '6px 12px',
                            cursor: 'pointer',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            background: '#fff',
                            fontSize: '14px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                          title="Zoom out"
                        >
                          <ZoomOut size={16} />
                        </button>
                        
                        <span style={{ minWidth: '60px', textAlign: 'center', fontWeight: 'bold' }}>
                          {Math.round(scale * 100)}%
                        </span>
                        
                        <button
                          onClick={() => setScale(Math.min(2.0, scale + 0.2))}
                          style={{
                            padding: '6px 12px',
                            cursor: 'pointer',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            background: '#fff',
                            fontSize: '14px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                          title="Zoom in"
                        >
                          <ZoomIn size={16} />
                        </button>
                        
                        <button
                          onClick={() => setScale(1.0)}
                          style={{
                            padding: '6px 12px',
                            cursor: 'pointer',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            background: '#fff',
                            fontSize: '12px'
                          }}
                          title="Reset zoom"
                        >
                          Reset
                        </button>
                      </div>
                      
                      {/* Divider */}
                      <div style={{ width: '1px', height: '24px', background: '#ddd' }}></div>
                      
                      {/* Print Button */}
                      <button
                        onClick={() => {
                          // Convert data URL to blob and create object URL
                          try {
                            fetch(pdfDataUrl)
                              .then(res => res.blob())
                              .then(blob => {
                                const blobUrl = URL.createObjectURL(blob);
                                const printWindow = window.open(blobUrl, '_blank');
                                if (printWindow) {
                                  printWindow.addEventListener('load', () => {
                                    setTimeout(() => {
                                      printWindow.print();
                                    }, 250);
                                  });
                                  // Clean up the blob URL after printing
                                  printWindow.addEventListener('afterprint', () => {
                                    URL.revokeObjectURL(blobUrl);
                                  });
                                }
                              })
                              .catch(err => {
                                console.error('Error printing PDF:', err);
                                alert('Failed to print PDF. Please try downloading instead.');
                              });
                          } catch (err) {
                            console.error('Error in print handler:', err);
                            alert('Failed to print PDF. Please try downloading instead.');
                          }
                        }}
                        style={{
                          padding: '8px 16px',
                          cursor: 'pointer',
                          border: 'none',
                          borderRadius: '4px',
                          background: '#4CAF50',
                          color: 'white',
                          fontSize: '14px',
                          fontWeight: 'bold',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px'
                        }}
                        title="Print PDF"
                      >
                        <Printer size={16} />
                        Print
                      </button>
                    </div>
                    
                    {/* PDF Page Container */}
                    <div style={{
                      background: '#fff',
                      padding: '20px',
                      display: 'flex',
                      justifyContent: 'center',
                      flex: 1,
                      overflowY: 'auto'
                    }}>
                      <div style={{
                        transform: `scale(${scale})`,
                        transformOrigin: 'top center',
                        transition: 'transform 0.2s ease-in-out'
                      }}>
                        <Document 
                          file={pdfDataUrl}
                          onLoadSuccess={onDocumentLoadSuccess}
                          loading={<p style={{ padding: '20px', textAlign: 'center' }}>Loading PDF...</p>}
                          error={<p style={{ padding: '20px', textAlign: 'center', color: 'red' }}>Failed to load PDF</p>}
                        >
                          <Page pageNumber={currentPage} width={612} />
                        </Document>
                      </div>
                    </div>
                  
                  {/* Navigation Controls */}
                  {numPages && (
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '15px',
                      backgroundColor: '#fff',
                      padding: '15px',
                      borderTop: '1px solid #ddd',
                      width: '100%'
                    }}>
                      <button
                        onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                        disabled={currentPage <= 1}
                        style={{
                          padding: '10px 16px',
                          cursor: currentPage <= 1 ? 'not-allowed' : 'pointer',
                          opacity: currentPage <= 1 ? 0.5 : 1,
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          background: '#fff',
                          fontSize: '14px'
                        }}
                      >
                        ← Previous Page
                      </button>
                      
                      <div style={{
                        display: 'flex',
                        gap: '8px',
                        alignItems: 'center'
                      }}>
                        <span style={{ fontSize: '14px', fontWeight: 'bold' }}>Page</span>
                        <input
                          type="number"
                          min="1"
                          max={numPages}
                          value={currentPage}
                          onChange={(e) => {
                            const page = Math.max(1, Math.min(numPages, parseInt(e.target.value) || 1));
                            setCurrentPage(page);
                          }}
                          style={{
                            width: '60px',
                            padding: '6px 8px',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            textAlign: 'center',
                            fontSize: '14px'
                          }}
                        />
                        <span style={{ fontSize: '14px' }}>of {numPages}</span>
                      </div>
                      
                      <button
                        onClick={() => setCurrentPage(Math.min(numPages, currentPage + 1))}
                        disabled={currentPage >= numPages}
                        style={{
                          padding: '10px 16px',
                          cursor: currentPage >= numPages ? 'not-allowed' : 'pointer',
                          opacity: currentPage >= numPages ? 0.5 : 1,
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          background: '#fff',
                          fontSize: '14px'
                        }}
                      >
                        Next Page →
                      </button>
                    </div>
                  )}
                </div>
                </div>
              </div>
            ) : (
              artworks.length > 0 && (
                <div className="wall-sections-container">
                  <h3>Organize Artworks by Wall Placement</h3>
                  <p className="help-text">
                    Edit the "Display Location" field to add custom text for each artwork's label.
                    This text will appear on the printed labels to help visitors locate artwork.
                  </p>
                  
                  {/* Display artworks grouped by wall */}
                  {walls.map(wall => {
                    const wallArtworks = artworksByWall[wall.element_id] || [];
                    if (wallArtworks.length === 0) return null;
                    
                    return (
                      <div key={wall.element_id} className="wall-section">
                        <div className="wall-header">
                          <h4>{wall.source || `Wall ${wall.element_id}`}</h4>
                          <div className="wall-info">
                            <span>{wallArtworks.length} artworks</span>
                            <span>•</span>
                            <span>{wall.width}w × {wall.height}h</span>
                          </div>
                        </div>
                        
                        <div className="artworks-rows">
                          {wallArtworks.map(artwork => (
                            <div key={artwork.vc_id} className="artwork-row-item">
                              <div className="artwork-row-image">
                                {artwork.cover_image && (
                                  <img 
                                    src={artwork.cover_image.replace('/upload/', '/upload/t_fitmax-300/')}
                                    alt={artwork.title}
                                  />
                                )}
                              </div>
                              <div className="artwork-row-details">
                                <div className="artwork-row-title">{artwork.title || 'Untitled'}</div>
                                <div className="artwork-row-artist">{artwork.nickname}</div>
                                <div className="artwork-row-size">
                                  {artwork.width || 0}w × {artwork.height || 0}h
                                  {showPrice && artwork.price && ` • $${artwork.price}`}
                                </div>
                                <button 
                                  className="display-location-link"
                                  onClick={() => setDisplayLocationModal(artwork.vc_id)}
                                >
                                  {editDisplayText[artwork.vc_id] || artwork.display_location || 'Add location'}
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                  
                  {/* Unassigned artworks */}
                  {artworksByWall.null && artworksByWall.null.length > 0 && (
                    <div className="wall-section">
                      <div className="wall-header">
                        <h4>Unassigned Artworks</h4>
                        <div className="wall-info">
                          <span>{artworksByWall.null.length} artworks</span>
                        </div>
                      </div>
                      
                      <div className="artworks-rows">
                        {artworksByWall.null.map(artwork => (
                          <div key={artwork.vc_id} className="artwork-row-item">
                            <div className="artwork-row-image">
                              {artwork.cover_image && (
                                <img 
                                  src={artwork.cover_image.replace('/upload/', '/upload/t_fitmax-300/')}
                                  alt={artwork.title}
                                />
                              )}
                            </div>
                            <div className="artwork-row-details">
                              <div className="artwork-row-title">{artwork.title || 'Untitled'}</div>
                              <div className="artwork-row-artist">{artwork.nickname}</div>
                              <div className="artwork-row-size">
                                {artwork.width || 0}w × {artwork.height || 0}h
                                {showPrice && artwork.price && ` • $${artwork.price}`}
                              </div>
                              <button 
                                className="display-location-link"
                                onClick={() => setDisplayLocationModal(artwork.vc_id)}
                              >
                                {editDisplayText[artwork.vc_id] || artwork.display_location || 'Add location'}
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )
            )}
            
            {artworks.length === 0 && !loading && !error && (
              <div className="empty-state">
                <h3>No Artworks Found</h3>
                <p>This collection doesn't have any artworks yet.</p>
                <Link to={`/admin/collections/${collectionId}`} className="button secondary-button">
                  Return to Collection
                </Link>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Display Location Modal */}
      {displayLocationModal && (
        <div className="location-modal-overlay" onClick={() => setDisplayLocationModal(null)}>
          <div className="location-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Set Display Location</h3>
            <input
              type="text"
              placeholder="e.g., North Wall, Main Hall"
              value={editDisplayText[displayLocationModal] || ''}
              onChange={(e) => handleDisplayTextChange(displayLocationModal, e.target.value)}
              autoFocus
            />
            <div className="location-modal-buttons">
              <button 
                className="secondary-button"
                onClick={() => setDisplayLocationModal(null)}
              >
                Cancel
              </button>
              <button 
                className="primary-button"
                onClick={() => {
                  saveDisplayText(displayLocationModal);
                  setDisplayLocationModal(null);
                }}
                disabled={isSaving}
              >
                {isSaving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ArtworkPdfGenerator;